import { Button, Select } from '@components'
import { TextTooltip } from '@components/InfoPopup'
import React, { useMemo } from 'react'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa'
import { FilterCondition, FilterNodeConfig } from '../nodes/types'
import { ConditionFields } from './FilterConditionFields'
import {
  createDefaultCondition,
  DEFAULT_FILTER_CONFIG,
  getFieldSpec,
  getFilterTypeSpec,
  normalizeConditionForField,
  normalizeConditionsForFilterType
} from '../nodes/filterConfigUtils'
import { optionIncludesValue } from './formOptionUtils'
import {
  AddButtonRow,
  ConditionCard,
  ConditionEntry,
  ConditionsSection,
  ConnectorChip,
  ConnectorRow,
  ConnectorRule,
  ErrorBlock,
  FormStack,
  HintText,
  RemoveButton,
  StatusText,
  TargetLabel,
  TargetRow,
  TargetSelect
} from './filterNodeConfigForm.styles'
import { useNodeValidationIssues } from './GraphValidationContext'
import { useWorkflowCatalogs } from './WorkflowCatalogsContext'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'

interface Props {
  nodeId: string
  config: FilterNodeConfig
  onChange: (nextConfig: FilterNodeConfig) => void
  variant: 'filter' | 'selector'
}

export const FilterNodeConfigForm: React.FC<Props> = ({ nodeId, config, onChange, variant }) => {
  const [t] = useTranslation()
  const isSelector = variant === 'selector'
  const nodeIssues = useNodeValidationIssues(nodeId)
  const filterTypeError = useMemo(
    () => nodeIssues.find(issue => issue.code === 'item_kind_mismatch')?.message,
    [nodeIssues]
  )
  const {
    filterCatalog: catalog,
    filterQuery: { isLoading, isError, refetch }
  } = useWorkflowCatalogs()
  const formValues = useMemo(
    () => ({
      ...DEFAULT_FILTER_CONFIG,
      ...config
    }),
    [config]
  )
  const { control, setValue, getValues, pushConfig } = useWorkflowConfigForm<FilterNodeConfig>({
    defaultValues: formValues,
    onChange
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions'
  })
  const filterType = useWatch({ control, name: 'filter_type' })
  const match = useWatch({ control, name: 'match' })

  const typeOptions = useMemo(
    () => (isSelector ? catalog.filter(option => option.value !== 'bank_transaction') : catalog),
    [catalog, isSelector]
  )
  const typeSpec = useMemo(() => getFilterTypeSpec(catalog, filterType), [catalog, filterType])

  const toggleMatch = () => {
    setValue('match', match === 'all' ? 'any' : 'all', {
      shouldDirty: true,
      shouldValidate: true
    })
    pushConfig()
  }

  const updateFilterType = (nextFilterType: string) => {
    const nextTypeSpec = getFilterTypeSpec(catalog, nextFilterType)
    if (!nextTypeSpec) return

    setValue('filter_type', nextFilterType, { shouldDirty: true, shouldValidate: true })
    setValue(
      'conditions',
      normalizeConditionsForFilterType(catalog, nextFilterType, getValues('conditions')),
      { shouldDirty: true, shouldValidate: true }
    )
    pushConfig()
  }

  const updateCondition = (index: number, patch: Partial<FilterCondition>) => {
    if (!typeSpec) return

    const condition = getValues('conditions')[index]
    if (!condition) return

    const merged = { ...condition, ...patch }
    const fieldSpec = getFieldSpec(typeSpec, merged.field)
    const normalized = fieldSpec ? normalizeConditionForField(merged, fieldSpec) : merged

    setValue(`conditions.${index}`, normalized, { shouldDirty: true, shouldValidate: true })
    pushConfig()
  }

  const addCondition = () => {
    if (!typeSpec) return

    const nextCondition = createDefaultCondition(typeSpec)
    if (!nextCondition) return

    append(nextCondition)
    pushConfig()
  }

  if (isLoading) {
    return <StatusText>{t('workflows.config.filter.loading')}</StatusText>
  }

  if (isError) {
    return (
      <ErrorBlock>
        <StatusText>{t('workflows.config.filter.loadError')}</StatusText>
        <Button type="button" isSecondary onClick={() => refetch()}>
          {t('workflows.config.filter.retry')}
        </Button>
      </ErrorBlock>
    )
  }

  if (!catalog.length) {
    return <StatusText>{t('workflows.config.filter.emptyCatalog')}</StatusText>
  }

  const connectorLabel =
    match === 'all'
      ? t('workflows.config.filter.connectorAnd')
      : t('workflows.config.filter.connectorOr')
  const connectorHint =
    match === 'all'
      ? t('workflows.config.filter.connectorHintAll')
      : t('workflows.config.filter.connectorHintAny')

  return (
    <FormStack>
      <TargetRow>
        <TargetLabel>
          {t(isSelector ? 'workflows.config.filter.target' : 'workflows.config.filter.targetType')}
        </TargetLabel>
        <Controller
          name="filter_type"
          control={control}
          render={({ field, fieldState }) => (
            <TargetSelect>
              <Select
                id={`workflow-filter-type-${nodeId}`}
                name={`workflow-filter-type-${nodeId}`}
                value={field.value}
                errors={
                  filterTypeError ? { message: filterTypeError, type: 'server' } : fieldState.error
                }
                onChange={event => updateFilterType(event.target.value)}
                onBlur={field.onBlur}
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {!optionIncludesValue(typeOptions, field.value) && field.value && (
                  <option value={field.value}>{field.value}</option>
                )}
              </Select>
            </TargetSelect>
          )}
        />
      </TargetRow>

      <ConditionsSection>
        {fields.map((_, index) => (
          // useFieldArray replaces field.id when this form echoes config back
          // through `values`, which remounts the row and drops the caret.
          <React.Fragment key={index}>
            {index > 0 && (
              <ConnectorRow>
                <ConnectorRule />
                <TextTooltip tooltip={connectorHint} openDelay={150} maxWidth={220}>
                  <ConnectorChip
                    type="button"
                    size="sm"
                    isSecondary
                    iconRight={<FaSyncAlt />}
                    onClick={toggleMatch}
                  >
                    {connectorLabel}
                  </ConnectorChip>
                </TextTooltip>
                <ConnectorRule />
              </ConnectorRow>
            )}

            <ConditionEntry>
              <ConditionCard>
                <ConditionFields
                  typeSpec={typeSpec}
                  control={control}
                  index={index}
                  onUpdate={updateCondition}
                />
                <RemoveButton
                  type="button"
                  size="sm"
                  isSecondary
                  intent="danger"
                  icon={<FaTrash />}
                  aria-label={t('workflows.config.filter.removeCondition')}
                  onClick={() => {
                    remove(index)
                    pushConfig()
                  }}
                />
              </ConditionCard>
            </ConditionEntry>
          </React.Fragment>
        ))}

        {fields.length === 0 && (
          <HintText>
            {t(
              isSelector
                ? 'workflows.config.selector.noConditions'
                : 'workflows.config.filter.noConditions'
            )}
          </HintText>
        )}

        <AddButtonRow>
          <Button type="button" size="sm" isSecondary icon={<FaPlus />} onClick={addCondition}>
            {t('workflows.config.filter.addCondition')}
          </Button>
        </AddButtonRow>
      </ConditionsSection>
    </FormStack>
  )
}
